import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type AdminClient } from '../../api/admin';
import { confirm } from '../../components/ConfirmDialog';

const empty = (): Partial<AdminClient> => ({ appVersion: '0.6.0' });

export default function AdminClientsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<null | 'create' | AdminClient>(null);
  const [form, setForm] = useState<Partial<AdminClient>>(empty());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: clients = [], isLoading } = useQuery({ queryKey: ['admin-clients', search], queryFn: () => adminApi.listClients(search || undefined), staleTime: 30 * 1000 });
  const del = useMutation({ mutationFn: adminApi.deleteClient, onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-clients'] }) });

  function openCreate() { setForm(empty()); setError(null); setModal('create'); }
  function openEdit(c: AdminClient) { setForm({ ...c }); setError(null); setModal(c); }

  async function handleSave() {
    if (!form.name?.trim()) { setError('Contact name is required'); return; }
    if (!form.business?.trim()) { setError('Business name is required'); return; }
    setError(null); setSaving(true);
    try {
      if (modal === 'create') await adminApi.createClient(form);
      else await adminApi.updateClient((modal as AdminClient).id, form);
      qc.invalidateQueries({ queryKey: ['admin-clients'] });
      qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
      setModal(null);
    } catch (err) { setError(err instanceof Error ? err.message : 'Save failed'); }
    finally { setSaving(false); }
  }

  function set(k: keyof AdminClient) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm(f => ({ ...f, [k]: e.target.value }));
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Clients</h1>
        <button className="btn btn-primary" onClick={openCreate}>+ New Client</button>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, business, phone…" style={{ maxWidth: 360 }} />
      </div>

      <div className="card">
        {isLoading ? <div className="loading-overlay"><span className="spinner" /></div>
          : clients.length === 0
          ? <div className="empty-state"><div className="empty-state-icon">👥</div><div className="empty-state-text">No clients yet</div></div>
          : (
            <table>
              <thead>
                <tr><th>Business</th><th>Contact</th><th>Phone</th><th>City</th><th>Version</th><th>Subs</th><th>Keys</th><th>Joined</th><th style={{ width: 100 }}>Actions</th></tr>
              </thead>
              <tbody>
                {clients.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600 }}>{c.business}</td>
                    <td>{c.name}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.83rem' }}>{c.phone ?? '—'}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.83rem' }}>{c.city ?? '—'}{c.state ? `, ${c.state}` : ''}</td>
                    <td><span className="badge badge-gray">{c.appVersion}</span></td>
                    <td style={{ textAlign: 'center' }}>{c._count?.subscriptions ?? 0}</td>
                    <td style={{ textAlign: 'center' }}>{c._count?.licenseKeys ?? 0}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.83rem' }}>{c.createdAt.slice(0, 10)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={async () => { if (await confirm({ message: `Delete "${c.business}"?`, danger: true })) del.mutate(c.id); }}>Del</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <span className="modal-title">{modal === 'create' ? 'New Client' : 'Edit Client'}</span>
              <button className="modal-close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {error && <div className="error-box">{error}</div>}
              <div className="form-row form-row-2">
                <label>Business Name *<input value={form.business ?? ''} onChange={set('business')} placeholder="Acme Traders" autoFocus /></label>
                <label>Contact Name *<input value={form.name ?? ''} onChange={set('name')} placeholder="Ravi Kumar" /></label>
              </div>
              <div className="form-row form-row-2">
                <label>Phone<input value={form.phone ?? ''} onChange={set('phone')} placeholder="+91 9876543210" /></label>
                <label>Email<input type="email" value={form.email ?? ''} onChange={set('email')} placeholder="ravi@acme.com" /></label>
              </div>
              <div className="form-row form-row-3">
                <label>City<input value={form.city ?? ''} onChange={set('city')} placeholder="Mumbai" /></label>
                <label>State<input value={form.state ?? ''} onChange={set('state')} placeholder="Maharashtra" /></label>
                <label>App Version<input value={form.appVersion ?? ''} onChange={set('appVersion')} placeholder="0.6.0" /></label>
              </div>
              <label style={{ fontWeight: 500, fontSize: '0.875rem', display: 'block' }}>Notes
                <input value={form.notes ?? ''} onChange={set('notes')} placeholder="Additional notes…" style={{ marginTop: '0.35rem' }} />
              </label>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Save Client'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
